import { useEffect, useState } from 'react';
import { Modal, Input, Space, Tag, Checkbox, Button, Popover, Popconfirm, Select, Tooltip, InputNumber } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import NdoeSpinner from '../../../components/ndoe/NdoeSpinner';
import { format } from 'date-fns';
import config from '../../../config';
import { useSelector } from 'react-redux';
import CountUp from 'react-countup';
import { toast } from 'react-toastify';
import { UserOutlined, ClockCircleOutlined, DeleteOutlined, WhatsAppOutlined } from '@ant-design/icons';
import 'react-toastify/dist/ReactToastify.css';
import { estOfferte, libellePrix } from '../../../utils/prixOfferte';
import canSeeFinance from '../../../utils/canSeeFinance';
import usePermissions from '../../../hooks/usePermissions';
import {
  NdoePage, NdoePageHeader, NdoeActionButton, NdoeResponsiveTable,
  NdoeDataCard, NdoeStatusBadge, NdoeStatCard, NdoeStats, NdoeLoading, NdoeImage,
} from '../../../components/ndoe';

const RESA_STATUT_LABEL = {
  AFFECTEE: 'Affectée', PRISE_EN_CHARGE: 'Prise en charge', EN_COURS: 'En cours',
  REPORTEE: 'Reportée', ECHANGE_EN_COURS: 'Échange en cours', ECHANGE_A_LIVRER: 'Échange à livrer',
};
const statutResaLabel = (s) => RESA_STATUT_LABEL[s] || s;
const cellHead = { padding: '6px 8px', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' };
const cellBody = { padding: '6px 8px', color: 'var(--ndoe-ink)', whiteSpace: 'nowrap' };

const ListeDetailView = () => {
  const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selected, setSelected] = useState([]);
  const [getLivreur, setGetLivreur] = useState([]);
  const [quantities, setQuantities] = useState([]);
  const [livreur, setLivreur] = useState('');
  const [totalPrice, setTotalPrice] = useState(0);
  const userId = useSelector((state) => state.user.currentUser.id);
  const [remise, setRemise] = useState(0);
  const [totalAvecRemise, setTotalAvecRemise] = useState(0);
  const [getCommande, setGetCommande] = useState([]);
  const user = useSelector((state) => state.user?.currentUser);
  const isAdmin = user?.role === 'admin';
  const showFinance = canSeeFinance(user);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { can, ready: permsReady } = usePermissions();
  // Vente directe (chantier #1) : au comptant ou à crédit, sans livreur.
  const [showVenteDirecte, setShowVenteDirecte] = useState(false);
  const [vdMontant, setVdMontant] = useState(0);
  const [vdMoyen, setVdMoyen] = useState('Espèces');
  const [vdSaving, setVdSaving] = useState(false);
  const [vdTotal, setVdTotal] = useState(0); // total de TOUTE la commande (pas seulement les lignes cochées)
  // Régularisation des dates (chantier #3) : corriger date commande / livraison, tracé.
  const [showRegul, setShowRegul] = useState(false);
  const [regDateCmd, setRegDateCmd] = useState('');
  const [regDateLiv, setRegDateLiv] = useState('');
  const [regMotifCode, setRegMotifCode] = useState(undefined);
  const [regMotif, setRegMotif] = useState('');            // texte libre (motif « autre »)
  const [regSaving, setRegSaving] = useState(false);
  // Config de régularisation (motifs SELECT, plancher paramétrable, permission exceptionnelle).
  const [regConfig, setRegConfig] = useState({ motifs: [], recul_max: 30, exceptionnel: false });
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  // Modal « paire déjà réservée » (alerter plutôt que bloquer) : blocages renvoyés
  // par l'API (409) + réservations cochées à libérer.
  const [blocages, setBlocages] = useState(null);
  const [releaseSel, setReleaseSel] = useState({});
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const id = pathname.split('/')[2];

  const handleSelectionChange = (event, id, prix, quantite, id_detail) => {
    if (event.target.checked) {
      setSelected((prev) => [...prev, { id, prix, quantite, id_detail }]);
      setQuantities((prev) => ({ ...prev, [id_detail]: quantite }));
    } else {
      setSelected((prev) => prev.filter((row) => row.id_detail !== id_detail));
      setQuantities((prev) => {
        const { [id_detail]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  // « Montant sélectionné » / « Prix total » = SOMME des (prix unitaire × quantité
  // à livrer) des lignes cochées. Number(...) est indispensable : les décimaux
  // arrivent en chaîne (mysql2) et « acc + item.prix » concaténait au lieu
  // d'additionner ; on multiplie aussi par la quantité (ignorée auparavant).
  useEffect(() => {
    const sum = selected.reduce((acc, item) => {
      const q = Number(quantities[item.id_detail]);
      const qty = Number.isFinite(q) && q > 0 ? q : Number(item.quantite) || 0;
      return acc + (Number(item.prix) || 0) * qty;
    }, 0);
    setTotalPrice(sum);
  }, [selected, quantities]);

  // Valide AVANT d'ouvrir la confirmation : la modale ne s'ouvre que si
  // l'affectation est réellement envoyable (sinon message précis).
  const handleConfirm = () => {
    // ⑥ : on efface les messages précédents pour ne jamais empiler d'anciennes
    // erreurs avec la validation qui suit.
    toast.dismiss();
    if (selected.length === 0) {
      toast.warning('Cochez d’abord au moins un article à livrer, puis choisissez un livreur.');
      return;
    }
    if (!livreur) {
      toast.warning('Choisissez le livreur à qui affecter la commande.');
      return;
    }
    for (const item of selected) {
      const qte = Number(quantities[item.id_detail]);
      if (!Number.isInteger(qte) || qte < 1 || qte > item.quantite) {
        toast.error(`Quantité à livrer invalide (entre 1 et ${item.quantite})`);
        return;
      }
    }
    setShowConfirmModal(true);
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
  };

  useEffect(() => {
    if (remise !== 0) {
      const totalAvecRemiseValue = totalPrice - remise;
      setTotalAvecRemise(totalAvecRemiseValue);
    } else {
      setTotalAvecRemise(totalPrice);
    }
  }, [remise, totalPrice, selected]);

  const fetchDetail = async () => {
    try {
      const { data } = await axios.get(`${DOMAIN}/api/commande/detail-commande/${id}`);
      setData(data);
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const handleDelete = async (idDetail) => {
    try {
      await axios.delete(`${DOMAIN}/api/commande/detail-commande/${idDetail}`);
      fetchDetail();   // refetch au lieu de recharger toute la page
    } catch (err) {
      console.log(err);
    }
  };

  // Une ligne est « engagée » (action décisive déjà passée) dès qu'elle porte un
  // statut de livraison, sauf si l'affectation a été libérée/expirée (auquel cas
  // elle redevient assignable). On grise alors sa case « à livrer » : garde-fou
  // anti-double-affectation, absent jusqu'ici côté écran (le serveur refusait en
  // 409, mais l'utilisateur ne le voyait qu'après coup).
  const estEngagee = (record) =>
    !!record.statut_livraison &&
    !['AFFECTATION_LIBEREE', 'AFFECTATION_EXPIREE'].includes(record.statut_livraison);

  const baseColumns = [
    {
      title: '',
      dataIndex: 'id_detail',
      key: 'selected',
      render: (text, record) => (
        <div>
          <Checkbox
            disabled={estEngagee(record)}
            checked={selected.some((item) => item.id_detail === record.id_detail)}
            onChange={(event) =>
              handleSelectionChange(event, record.id_varianteProduit, record.prix, record.quantite, record.id_detail)
            }
          />
          {selected.some((item) => item.id_detail === record.id_detail) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ color: '#555' }}>Qté à livrer</label>
              <Input
                value={quantities[record.id_detail]}
                onChange={(event) =>
                  setQuantities((prevQuantities) => ({
                    ...prevQuantities,
                    [record.id_detail]: event.target.value,
                  }))
                }
                style={{ width: '65px' }}
              />
            </div>
          )}
        </div>
      ),
    },
    { title: '#', dataIndex: 'id', key: 'id', render: (text, record, index) => index + 1, width: "3%" },
    { title: 'code',
      dataIndex: 'id',
      key: 'code',
      render: (text, record, index) => (
        <div>
           <Tag color="blue">{`${new Date().getFullYear().toString().substring(2)}${record.id_detail.toString().padStart(4, '0')}`}</Tag>
        </div>
      ), width: "3%" },
    {
      title: 'image',
      dataIndex: 'img',
      key: 'img',
      render: (text, record) => (
        <NdoeImage src={record.img} alt={record.nom_produit || `Pointure ${record.taille ?? ''}`.trim()} size="lg" />
      ),
    },
    {
      // Mandat §6 : référence TOUJOURS visible à côté du libellé.
      title: 'Article',
      dataIndex: 'nom_produit',
      key: 'article',
      render: (text, record) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {(record.code_article || record.code_produit) && (
            <span className="num" style={{ color: '#8a5a10', fontWeight: 700, fontSize: 12 }}>
              {record.code_article || record.code_produit}
            </span>
          )}
          <span>{record.nom_produit || '—'}</span>
        </div>
      ),
    },
    {
      title: 'Quantité',
      dataIndex: 'quantite',
      key: 'quantite',
      render: (text) => (
        <Space>
          <Tag color="green">{text}</Tag>
        </Space>
      ),
    },
    {
      title: 'Pointure',
      dataIndex: 'taille',
      key: 'id_taille',
      render: (text) => (
        <Space>
          <Tag color="orange">{text}</Tag>
        </Space>
      ),
    },
    {
      // Statut de livraison PAR LIGNE (18/07) : badge couleur + livreur affecté.
      // Statut null = ligne jamais affectée (encore « à préparer »).
      title: 'Statut livraison',
      dataIndex: 'statut_livraison',
      key: 'statut_livraison',
      render: (statut, record) => (
        statut ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
            <NdoeStatusBadge kind="livraison" status={statut} />
            {record.nom_livreur ? (
              <span style={{ fontSize: 11, color: 'var(--ndoe-muted, #5d6b80)' }}>
                Affecté à <b>{record.nom_livreur}</b>
              </span>
            ) : null}
          </div>
        ) : (
          <Tag color="default">À préparer</Tag>
        )
      ),
    },
    {
      title: 'Prix',
      dataIndex: 'prix',
      key: 'prix',
      finance: true,
      sorter: (a, b) => a.prix - b.prix,
      sortDirections: ['descend', 'ascend'],
      // ⚠ « 0,00 $ » en vert se lit comme un prix oublié. Une paire OFFERTE est
      // une décision commerciale : on la nomme, et on montre son motif.
      render: (text, record) => (
        <span>
          <Tooltip title={estOfferte(text) ? (record?.motif_gratuite || 'Paire offerte') : ''}>
            <Tag color={estOfferte(text) ? 'gold' : 'green'}>
              {libellePrix(text)}
            </Tag>
          </Tooltip>
        </span>
      ),
    },
    {
      title: 'Date demande & heure',
      dataIndex: 'date_demande',
      key: 'date_demande',
      render: (text) => {
        const formattedDate = format(new Date(text), 'dd-MM-yyyy HH:mm:ss');
        return (
          <Tag color="green">
            <ClockCircleOutlined style={{ marginRight: '5px' }} />
            <span>{formattedDate}</span>
          </Tag>
        );
      },
    },
    {
      title: 'Créé par',
      dataIndex: 'username',
      key: 'username',
      render: (text) => (
        <Tag color="blue"><UserOutlined style={{ marginRight: "5px" }} />{text}</Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <Space size="middle">
          {isAdmin &&
          <Popover title="Supprimer" trigger="hover">
            <Popconfirm
              title="Êtes-vous sûr de vouloir supprimer?"
              onConfirm={() => handleDelete(record.id_detail)}
              okText="Oui"
              cancelText="Non"
            >
              <Button icon={<DeleteOutlined />} style={{ color: 'red' }} />
            </Popconfirm>
          </Popover> }
        </Space>
      ),
    },
  ];

  // Retire la colonne prix (finance) pour les rôles non autorisés.
  const columns = baseColumns.filter((c) => !c.finance || showFinance);

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DOMAIN, id]);

  useEffect(() => {
    const fetchLivreur = async () => {
      try {
        const { data } = await axios.get(`${DOMAIN}/api/livreur`);
        setGetLivreur(data);
        setLoading(false);
      } catch (error) {
        console.log(error);
      }
    };
    fetchLivreur();
  }, [DOMAIN]);

  useEffect(() => {
    const fetchCommande = async () => {
      try {
        const { data } = await axios.get(`${DOMAIN}/api/commande/commandeOne/${id}`);
        setGetCommande(data[0]);
      } catch (error) {
        console.log(error);
      }
    };
    fetchCommande();
  }, [DOMAIN, id]);

  // Affectation ATOMIQUE : toutes les paires cochées partent en UNE requête
  // transactionnelle (mission unique, anti-double, contrôle du stock disponible).
  // Affectation. Un 409 { blocages } (paire déjà réservée) ouvre le modal d'alerte
  // au lieu d'un toast bloquant : le staff peut forcer ou libérer une réservation.
  const submitAffectation = async ({ force = false, release = [] } = {}) => {
    if (selected.length === 0 || !livreur) {
      toast.error('Cochez au moins un article et sélectionnez un livreur');
      setShowConfirmModal(false);
      return;
    }
    setIsLoading(true);
    try {
      const { data } = await axios.post(`${DOMAIN}/api/livraison/affectation`, {
        id_commande: id,
        id_livreur: livreur,
        id_user_cr: userId,
        lines: selected.map((dd) => ({ id_detail_commande: dd.id_detail, qte: Number(quantities[dd.id_detail]) })),
        force,
        release,
      });
      const libTxt = data.liberees ? `, ${data.liberees} réservation(s) libérée(s)` : '';
      toast.success(`Mission ${data.numero} créée : ${data.affectees} article(s) affecté(s)${libTxt}`);
      setShowConfirmModal(false);
      setBlocages(null);
      // ⑧ Enchaînement : après l'affectation on bascule sur la liste des
      // livraisons (statuts visibles) plutôt que sur la liste des commandes,
      // pour suivre la mission qu'on vient de créer.
      navigate('/livraison_detail');
    } catch (error) {
      const resp = error?.response?.data;
      if (error?.response?.status === 409 && Array.isArray(resp?.blocages)) {
        setShowConfirmModal(false);
        setReleaseSel({});
        setBlocages(resp.blocages);
      } else {
        toast.error(resp?.error || "Erreur lors de l'affectation de la livraison");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = (e) => { e.preventDefault(); submitAffectation(); };

  // Vente directe : sur TOUTE la commande (la vente directe vend l'intégralité).
  // Total = somme de toutes les lignes ; montant par défaut = total (comptant plein).
  const openVenteDirecte = () => {
    const full = (Array.isArray(data) ? data : []).reduce((s, line) => s + (Number(line.prix) || 0) * (Number(line.quantite) || 0), 0);
    setVdTotal(full);
    setVdMontant(full);
    setVdMoyen('Espèces');
    setShowVenteDirecte(true);
  };
  const confirmVenteDirecte = async () => {
    setVdSaving(true);
    try {
      await axios.post(`${DOMAIN}/api/commande/vente-directe/${id}`, { montant_paye: vdMontant, moyen: vdMoyen });
      toast.success('Vente directe enregistrée.');
      setShowVenteDirecte(false);
      navigate('/listeCommande');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Vente directe refusée.');
    } finally { setVdSaving(false); }
  };

  // Régularisation (chantier #3) : préremplit la date commande avec l'existante.
  // date_commande_jour vient du serveur déjà en 'yyyy-MM-dd' (DATE_FORMAT, sans
  // aléa de fuseau) — pas de new Date() local qui décalerait d'un jour.
  // Plancher de recul (min autorisé) — sauf permission exceptionnelle (date libre).
  const regMinStr = (() => {
    if (regConfig.exceptionnel) return undefined;
    const d = new Date(); d.setDate(d.getDate() - (Number(regConfig.recul_max) || 0));
    return format(d, 'yyyy-MM-dd');
  })();
  const regMaxStr = regConfig.exceptionnel ? undefined : todayStr;

  const openRegul = async () => {
    setRegDateCmd(getCommande?.date_commande_jour || todayStr);
    setRegDateLiv('');
    setRegMotifCode(undefined);
    setRegMotif('');
    setShowRegul(true);
    // Motifs + plancher + permission exceptionnelle (le serveur reste la garde réelle).
    try {
      const { data } = await axios.get(`${DOMAIN}/api/commande/regul-config`);
      setRegConfig({
        motifs: Array.isArray(data?.motifs) ? data.motifs : [],
        recul_max: Number(data?.recul_max) || 30,
        exceptionnel: !!data?.exceptionnel,
      });
    } catch {
      // Fallback si le fetch échoue : motifs par défaut (codes alignés sur REGUL_MOTIFS
      // côté serveur) pour que le Select reste utilisable/soumettable hors-ligne.
      setRegConfig({
        motifs: [
          { code: 'saisie_tardive', label: 'Saisie tardive de la commande' },
          { code: 'erreur_date', label: 'Erreur de date à la saisie' },
          { code: 'correction_verif', label: 'Correction après vérification' },
          { code: 'oubli_enregistrement', label: 'Oubli d’enregistrement à la date réelle' },
          { code: 'autre', label: 'Autre (préciser)' },
        ],
        recul_max: 30,
        exceptionnel: false,
      });
    }
  };
  const confirmRegul = async () => {
    if (!regDateCmd) { toast.error('Indiquez la date de commande.'); return; }
    if (!regMotifCode) { toast.error('Choisissez un motif.'); return; }
    if (regMotifCode === 'autre' && regMotif.trim().length < 5) { toast.error('Précisez le motif (au moins 5 caractères).'); return; }
    if (regDateLiv && regDateLiv < regDateCmd) { toast.error('La date de livraison ne peut pas être antérieure à la commande.'); return; }
    if (!regConfig.exceptionnel) {
      if (regDateCmd > todayStr) { toast.error('La date de commande ne peut pas être dans le futur.'); return; }
      if (regDateLiv && regDateLiv > todayStr) { toast.error('La date de livraison ne peut pas être dans le futur.'); return; }
      if (regMinStr && regDateCmd < regMinStr) { toast.error(`Date trop ancienne (recul maximal ${regConfig.recul_max} j).`); return; }
    }
    setRegSaving(true);
    try {
      const body = { date_commande: regDateCmd, motif_code: regMotifCode };
      if (regDateLiv) body.date_livraison = regDateLiv;
      if (regMotifCode === 'autre') body.motif = regMotif.trim();
      await axios.put(`${DOMAIN}/api/commande/regulariser-date/${id}`, body);
      toast.success('Date(s) régularisée(s).');
      setShowRegul(false);
      // Recharge l'en-tête pour refléter la nouvelle date.
      try { const { data: c } = await axios.get(`${DOMAIN}/api/commande/commandeOne/${id}`); setGetCommande(c[0]); } catch { /* noop */ }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Régularisation refusée.');
    } finally { setRegSaving(false); }
  };

  const toggleRelease = (idl) => setReleaseSel((prev) => ({ ...prev, [idl]: !prev[idl] }));
  const selectedReleaseIds = Object.keys(releaseSel).filter((k) => releaseSel[k]).map(Number);

  const phone = getCommande?.telephone;
  const adresse = getCommande ? `Commune ${getCommande.nom_commune ?? '—'} · Av/ ${getCommande.avenue ?? ''} Q/ ${getCommande.quartier ?? ''} N° ${getCommande.num ?? ''}` : '';
  // Date de commande (chantier #3) : affichée dans l'en-tête ; se rafraîchit après régularisation.
  const dateCmdStr = getCommande?.date_commande_jour
    ? `Commandé le ${getCommande.date_commande_jour}${Number(getCommande?.date_regularisee) ? ' · régularisée' : ''}`
    : '';

  // --- Carte article à livrer (mobile) : reproduit exactement la sélection ---
  const renderDetailCard = (record) => {
    const isSel = selected.some((item) => item.id_detail === record.id_detail);
    const prixFmt = libellePrix(record.prix);
    const metrics = [
      { value: record.quantite, label: 'qté cmd' },
      { value: record.taille, label: 'pointure' },
    ];
    // Une paire offerte porte son motif en légende : sans lui, « Offerte »
    // n'explique rien à qui lit la commande six mois plus tard.
    if (showFinance) metrics.push({ value: prixFmt, label: estOfferte(record.prix) ? (record.motif_gratuite || 'offerte') : 'prix' });

    return (
      <NdoeDataCard
        media={<NdoeImage src={record.img} alt={record.nom_produit || `Pointure ${record.taille ?? ''}`.trim()} size="lg" />}
        title={record.nom_produit
          ? `${record.code_article || record.code_produit || ''}${record.code_article || record.code_produit ? ' – ' : ''}${record.nom_produit}`
          : `Pointure ${record.taille}`}
        subtitle={
          record.nom_livreur
            ? `Affecté à ${record.nom_livreur}`
            : (record.username ? `Créé par ${record.username}` : undefined)
        }
        badge={
          record.statut_livraison
            ? <NdoeStatusBadge kind="livraison" status={record.statut_livraison} />
            : (isSel ? <NdoeStatusBadge kind="custom" tone="ok" label="Sélectionné" /> : null)
        }
        metrics={metrics}
        actions={isAdmin ? [
          <Popconfirm key="d" title="Supprimer cet article ?" onConfirm={() => handleDelete(record.id_detail)} okText="Oui" cancelText="Non">
            <NdoeActionButton size="sm" variant="danger" icon={<DeleteOutlined />}>Suppr.</NdoeActionButton>
          </Popconfirm>,
        ] : []}
      >
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Checkbox
            disabled={estEngagee(record)}
            checked={isSel}
            onChange={(event) =>
              handleSelectionChange(event, record.id_varianteProduit, record.prix, record.quantite, record.id_detail)
            }
          >
            À livrer
          </Checkbox>
          {isSel && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: 'var(--ndoe-muted)' }}>Qté à livrer</span>
              <Input
                value={quantities[record.id_detail]}
                onChange={(event) =>
                  setQuantities((prev) => ({ ...prev, [record.id_detail]: event.target.value }))
                }
                style={{ width: 70 }}
              />
            </span>
          )}
        </div>
      </NdoeDataCard>
    );
  };

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Livraison"
        title={`Commande N° ${id}`}
        subtitle={`${getCommande?.nom ? getCommande.nom + ' — ' : ''}${adresse}${dateCmdStr ? ' · ' + dateCmdStr : ''}`}
        actions={phone ? (
          <NdoeActionButton variant="secondary" icon={<WhatsAppOutlined />} as="a" href={`https://wa.me/${String(phone).replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer">
            {phone}
          </NdoeActionButton>
        ) : null}
      />

      {showFinance && (
        <NdoeStats>
          <NdoeStatCard gold label="Montant sélectionné" value={<><CountUp end={Number(totalAvecRemise) || 0} /> $</>} />
        </NdoeStats>
      )}

      {loading ? (
        <NdoeLoading rows={4} />
      ) : (
        <>
          <NdoeResponsiveTable
            data={data}
            columns={columns}
            rowKey="id_detail"
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{ pageSize: 10 }}
            renderCard={renderDetailCard}
            /* Ligne engagée (déjà affectée/livrée) : léger fond grisé pour la
               signaler comme « verrouillée », SANS opacité globale — le badge de
               statut reste en pleine couleur, comme demandé. */
            onRow={(record) => (estEngagee(record) ? { style: { background: 'rgba(93,107,128,0.06)' } } : {})}
            empty={{ icon: '📦', title: 'Aucun article', text: 'Cette commande n’a pas de détail.' }}
          />

          {/* Panneau d'affectation */}
          <div className="ndoe-card" style={{ marginTop: 16 }}>
            <div className="ndoe-section__title">Affectation à un livreur</div>
            <div className="ndoe-form-grid">
              <div className="form-controle">
                <label>Livreur</label>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Sélectionnez un livreur"
                  value={livreur || undefined}
                  onChange={(val) => setLivreur(val)}
                  options={getLivreur.map((dd) => ({ value: dd.id, label: dd.username }))}
                />
              </div>
              {showFinance && (
                <div className="form-controle">
                  <label>La remise</label>
                  <Input
                    type="number"
                    value={remise}
                    onChange={(e) => {
                      const remiseValue = parseInt(e.target.value);
                      setRemise(e.target.value);
                      if (!isNaN(remiseValue) && remiseValue !== 0) {
                        setTotalAvecRemise(totalPrice - remiseValue);
                      } else {
                        setTotalAvecRemise(totalPrice);
                      }
                    }}
                  />
                </div>
              )}
              {showFinance && (
                <div className="form-controle">
                  <label>Prix total</label>
                  <h3 style={{ margin: 0, color: 'var(--ndoe-ink)' }}>{isFinite(totalAvecRemise) ? totalAvecRemise : ''} $</h3>
                </div>
              )}
            </div>

            <div style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <NdoeActionButton variant="primary" onClick={handleConfirm} loading={isLoading}>
                Affecter à un livreur
              </NdoeActionButton>
              <Tooltip title={!permsReady ? 'Vérification de vos droits…' : (can('ventes.vente_directe') ? 'Encaisser au comptant ou à crédit, sans livreur' : 'Vente directe non activée pour votre compte')}>
                <span>
                  <NdoeActionButton variant="ink" disabled={!permsReady || !can('ventes.vente_directe')} onClick={openVenteDirecte}>
                    Vendre directement
                  </NdoeActionButton>
                </span>
              </Tooltip>
              {permsReady && can('dates.regulariser') && (
                <Tooltip title="Corriger la date de commande / livraison (tracé)">
                  <span>
                    <NdoeActionButton variant="secondary" onClick={openRegul}>
                      Régulariser la date
                    </NdoeActionButton>
                  </span>
                </Tooltip>
              )}
            </div>
          </div>

          {/* Vente directe (chantier #1) : au comptant ou à crédit, sans livreur. */}
          <Modal
            title="Vente directe"
            open={showVenteDirecte}
            onOk={confirmVenteDirecte}
            onCancel={() => setShowVenteDirecte(false)}
            okText="Enregistrer la vente"
            cancelText="Annuler"
            confirmLoading={vdSaving}
          >
            <p style={{ marginTop: 0 }}>Vente de <b>toute la commande</b>. Total : <b>{Number(vdTotal) || 0} $</b>. Montant encaissé maintenant — laissez-le inférieur au total pour une vente à crédit.</p>
            <div className="form-controle">
              <label>Montant encaissé</label>
              <InputNumber inputMode="decimal" style={{ width: '100%' }} min={0} max={Number(vdTotal) || 0} value={vdMontant} onChange={(v) => setVdMontant(v ?? 0)} addonAfter="$" autoFocus />
            </div>
            <div className="form-controle" style={{ marginTop: 10 }}>
              <label>Moyen de paiement</label>
              <Select style={{ width: '100%' }} value={vdMoyen} onChange={setVdMoyen}
                options={['Espèces', 'Mobile Money', 'Virement', 'Autre'].map((m) => ({ value: m, label: m }))} />
            </div>
            <p style={{ marginTop: 10, fontSize: 12, color: '#a86f14' }}>
              Solde restant (crédit) : <b>{Math.max(0, (Number(vdTotal) || 0) - (Number(vdMontant) || 0))} $</b>
            </p>
          </Modal>

          {/* Régularisation des dates (chantier #3) : correction tracée. Règle
              date_commande ≤ date_livraison ≤ aujourd'hui (validée aussi côté serveur). */}
          <Modal
            title="Régulariser la date"
            open={showRegul}
            onOk={confirmRegul}
            onCancel={() => setShowRegul(false)}
            okText="Enregistrer"
            cancelText="Annuler"
            confirmLoading={regSaving}
          >
            <p style={{ marginTop: 0, fontSize: 13, color: 'var(--ndoe-muted)' }}>
              Correction des dates de cette commande. La commande sera marquée « régularisée » et l’opération est tracée + notifiée (qui, quand, avant → après, motif).
            </p>
            {regConfig.exceptionnel ? (
              <div style={{ marginBottom: 10, padding: '8px 10px', borderRadius: 8, background: '#fff7e6', border: '1px solid #ffd591', fontSize: 12.5, color: '#ad6800' }}>
                Mode <b>exceptionnel</b> (admin) : date libre, bornes levées. L’historique caisse (ventes / créances) n’est jamais modifié.
              </div>
            ) : (
              <div style={{ marginBottom: 10, fontSize: 12.5, color: 'var(--ndoe-muted)' }}>
                Recul maximal autorisé : <b>{regConfig.recul_max} jour(s)</b>. Une commande déjà vendue n’est pas régularisable ici.
              </div>
            )}
            <div className="form-controle">
              <label>Date de commande</label>
              <Input type="date" value={regDateCmd} min={regMinStr} max={regMaxStr} onChange={(e) => setRegDateCmd(e.target.value)} />
            </div>
            <div className="form-controle" style={{ marginTop: 10 }}>
              <label>Date de livraison <span style={{ color: 'var(--ndoe-muted)', fontWeight: 400 }}>(optionnel — s’applique aux lignes déjà livrées)</span></label>
              <Input type="date" value={regDateLiv} min={regDateCmd || regMinStr} max={regMaxStr} onChange={(e) => setRegDateLiv(e.target.value)} />
            </div>
            <div className="form-controle" style={{ marginTop: 10 }}>
              <label>Motif <span style={{ color: '#cf1322', fontWeight: 400 }}>*</span></label>
              <Select
                style={{ width: '100%' }}
                placeholder="Choisir un motif…"
                value={regMotifCode}
                onChange={setRegMotifCode}
                options={regConfig.motifs.map((m) => ({ value: m.code, label: m.label }))}
              />
            </div>
            {regMotifCode === 'autre' && (
              <div className="form-controle" style={{ marginTop: 10 }}>
                <label>Préciser le motif <span style={{ color: '#cf1322', fontWeight: 400 }}>*</span></label>
                <Input.TextArea rows={2} value={regMotif} maxLength={200} onChange={(e) => setRegMotif(e.target.value)} placeholder="Motif détaillé (au moins 5 caractères)…" />
              </div>
            )}
          </Modal>

          <Modal
            title="Confirmation"
            open={showConfirmModal}
            onOk={handleClick}
            onCancel={handleCancel}
            confirmLoading={isLoading}
            centered
            cancelText={<span style={{ color: '#fff' }}>Non</span>}
            okText={<span style={{ color: '#fff' }}>Oui</span>}
            cancelButtonProps={{ style: { background: 'red' } }}
            okButtonProps={{ style: { background: 'blue' } }}
            className="confirmation-modal"
          >
            <p>Voulez-vous vraiment effectuer cette action ?</p>
            {selected.length > 0 && (
              <div>
                <h4>Produits sélectionnés :</h4>
                <ul>
                  {selected.map((item) => (
                    <li key={item.id_detail}>
                      {estOfferte(item.prix)
                        ? `${item.quantite} x offerte = 0 $`
                        : `${item.quantite} x ${item.prix} $ = ${item.quantite * item.prix} $`}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {isLoading && (
              <div className="loader-container loader-container-center">
                <NdoeSpinner size={28} />
              </div>
            )}
          </Modal>

          {/* Alerte « paire déjà réservée » — informer sans bloquer : forcer, ou
              libérer une réservation existante pour donner la priorité à cette commande. */}
          <Modal
            open={!!blocages}
            onCancel={() => setBlocages(null)}
            footer={null}
            centered
            width={760}
            title={null}
            className="ndoe-blocage-modal"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <span style={{ fontSize: 22 }} aria-hidden="true">⚠️</span>
              <h3 style={{ margin: 0, color: 'var(--ndoe-ink)' }}>Paire(s) déjà réservée(s) ailleurs</h3>
            </div>
            <p style={{ color: 'var(--ndoe-muted)', marginTop: 0 }}>
              L’affectation n’est pas bloquée. Vous pouvez <b>forcer</b> (assumer la sur-affectation),
              ou <b>libérer</b> une réservation ci-dessous pour donner la priorité à cette commande.
            </p>

            {(blocages || []).map((b) => (
              <div key={b.id_varianteProduit} className="ndoe-card" style={{ marginBottom: 12, padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                  <strong style={{ color: 'var(--ndoe-ink)' }}>{b.produit}</strong>
                  <span style={{ fontSize: 12, color: 'var(--ndoe-muted)' }}>
                    {b.stock} en stock · demandé {b.demande} · disponible {b.disponible}
                  </span>
                </div>
                {b.reservations && b.reservations.length ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ textAlign: 'left', color: 'var(--ndoe-muted)', borderBottom: '1px solid var(--ndoe-border)' }}>
                          <th style={cellHead}>Libérer</th>
                          <th style={cellHead}>Commande</th>
                          <th style={cellHead}>Cliente</th>
                          <th style={cellHead}>Réservé le</th>
                          <th style={cellHead}>Livreur</th>
                          <th style={cellHead}>Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {b.reservations.map((rv) => (
                          <tr key={rv.id_detail_livraison} style={{ borderTop: '1px solid var(--ndoe-border)' }}>
                            <td style={cellBody}>
                              <Checkbox checked={!!releaseSel[rv.id_detail_livraison]} onChange={() => toggleRelease(rv.id_detail_livraison)} />
                            </td>
                            <td style={cellBody}>#{rv.id_commande}</td>
                            <td style={cellBody}>{rv.cliente}</td>
                            <td style={cellBody}>{rv.reserve_le}</td>
                            <td style={cellBody}>{rv.livreur}</td>
                            <td style={cellBody}><Tag color="gold">{statutResaLabel(rv.statut)}</Tag></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <small style={{ color: 'var(--ndoe-danger, #c0392b)' }}>
                    Rupture réelle : aucune réservation à libérer. Réapprovisionnez, ou forcez l’affectation.
                  </small>
                )}
              </div>
            ))}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 8 }}>
              <NdoeActionButton variant="secondary" onClick={() => setBlocages(null)}>Annuler</NdoeActionButton>
              <NdoeActionButton variant="ink" onClick={() => submitAffectation({ force: true })} loading={isLoading}>
                Forcer l’affectation
              </NdoeActionButton>
              <NdoeActionButton
                variant="primary"
                disabled={selectedReleaseIds.length === 0}
                onClick={() => submitAffectation({ release: selectedReleaseIds })}
                loading={isLoading}
              >
                {`Libérer${selectedReleaseIds.length ? ` (${selectedReleaseIds.length})` : ''} & affecter`}
              </NdoeActionButton>
            </div>
          </Modal>
        </>
      )}
    </NdoePage>
  );
};

export default ListeDetailView;
